import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, Loader2 } from 'lucide-react';

/**
 * ConnectionStatus — Persistent banner when offline + retry indicator.
 * Shows at the top of the viewport when connectivity is lost.
 * Automatically dismisses when connection resumes.
 */
const ConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setShowReconnected(true);
        setTimeout(() => setShowReconnected(false), 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  if (isOnline && !showReconnected) return null;

  return (
    <div
      role="status"
      aria-live="assertive"
      className={`fixed top-0 left-0 right-0 z-[200] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold transition-all ${
        isOnline
          ? 'bg-emerald-500 text-white'
          : 'bg-red-600 text-white'
      }`}
    >
      {isOnline ? (
        <>
          <Wifi className="w-4 h-4" aria-hidden="true" />
          Back online — your data is syncing
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4" aria-hidden="true" />
          No internet connection — reports will be saved and sent when you reconnect
          <Loader2 className="w-4 h-4 animate-spin ml-1" aria-hidden="true" />
        </>
      )}
    </div>
  );
};

export default ConnectionStatus;
